import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useContent } from "../../lib/content";
import { toast } from "sonner";
import { Save, RotateCcw, Mail, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

// UI grouping for clarity — keys must match backend DEFAULT_SITE_CONTENT
const GROUPS = [
  {
    label: "Felső sáv (Topbar)",
    fields: [
      { key: "topbar_phone_label", label: "Telefon címke", placeholder: "HÍVJON" },
      { key: "topbar_phone", label: "Telefonszám" },
      { key: "topbar_notice", label: "Piros figyelmeztetés", textarea: true },
    ],
  },
  {
    label: "Fő fejléc",
    fields: [
      { key: "header_logo_initials", label: "Logó iniciálék (rövid)", hint: "Max 3 karakter" },
      { key: "header_company_name", label: "Cégnév" },
      { key: "header_company_subtitle", label: "Alcím" },
      { key: "header_price_period", label: "Aktuális ár-időszak felirat (1. sor)" },
      { key: "header_price_subtext", label: "Felhívás (2. sor)", textarea: true },
      { key: "header_price_subtext2", label: "Felhívás (3. sor)", textarea: true },
      { key: "akcio_label", label: "AKCIÓ pecsét szövege" },
    ],
  },
  {
    label: "Katalógus hero",
    fields: [
      { key: "catalog_hero_eyebrow", label: "Eyebrow (kis felirat)" },
      { key: "catalog_hero_title", label: "Főcím" },
      { key: "catalog_hero_subtitle", label: "Alcím" },
      { key: "catalog_hero_notice", label: "Piros figyelmeztetés a hero-ban" },
    ],
  },
  {
    label: "Bejelentkezés / Regisztráció",
    fields: [
      { key: "login_welcome_title", label: "Bejelentkezés főcím" },
      { key: "login_returning_title", label: "Visszatérő vendég szekció címe" },
      { key: "login_new_customer_title", label: "Új ügyfél szekció címe" },
      { key: "login_new_customer_body", label: "Új ügyfél leírás", textarea: true },
    ],
  },
  {
    label: "Pénznem (Currency)",
    fields: [
      {
        key: "currency_default",
        label: "Alapértelmezett pénznem (EUR vagy HUF)",
        hint: "Ezt fogja látni minden új látogató. Bárki átkapcsolhatja a navbar EUR/Ft kapcsolóval.",
      },
      {
        key: "currency_huf_rate",
        label: "EUR → HUF árfolyam",
        hint: "Pl. 400 (azaz 1 EUR = 400 Ft). Ez alapján számoljuk a forint árakat.",
      },
    ],
  },
  {
    label: "Árazás — beégetett árrés (%)",
    fields: [
      {
        key: "markup_bmw_percent",
        label: "BMW árrés (%)",
        hint: "Pl. 15 → minden BMW termék base_price = beszállítói ár × 1,15. Új feltöltéseknél azonnal érvényes, a meglévő készletre az „Aktív készletforrások” alatt található átszámító gomb hat.",
      },
      {
        key: "markup_mercedes_percent",
        label: "Mercedes árrés (%)",
        hint: "Pl. 15 → minden Mercedes (PKW + LKW) termék base_price = Preis × 1,15. Műhely-szintű customer árrés Mercedesre is letiltva.",
      },
    ],
  },
  {
    label: "Lábléc (Footer)",
    fields: [
      { key: "footer_text", label: "Lábléc szöveg" },
      { key: "footer_links", label: "Lábléc linkek" },
    ],
  },
  {
    label: "Nyitólap (Landing — / útvonal)",
    fields: [
      { key: "landing_company_name", label: "Cégnév (logó alatt + footer)" },
      { key: "landing_tagline", label: "Szolgáltatás slogan (kék felirat)" },
      { key: "landing_service_1", label: "1. szolgáltatás" },
      { key: "landing_service_2", label: "2. szolgáltatás" },
      { key: "landing_service_3", label: "3. szolgáltatás" },
      { key: "landing_service_4", label: "4. szolgáltatás" },
      { key: "landing_service_5", label: "5. szolgáltatás" },
      { key: "landing_service_6", label: "6. szolgáltatás" },
      { key: "landing_service_7", label: "7. szolgáltatás", hint: "Üresen hagyva eltűnik" },
      { key: "landing_address", label: "Cím" },
      { key: "landing_hours", label: "Nyitvatartás" },
      { key: "landing_phone", label: "Telefonszám" },
      { key: "landing_email", label: "E-mail cím" },
    ],
  },
];

export default function AdminContent() {
  const { refresh } = useContent();
  const [values, setValues] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/site-content");
      setValues(data.values || {});
      setDefaults(data.defaults || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-content", { values });
      toast.success("Szövegek elmentve");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resetField = (key) => {
    setValues((v) => ({ ...v, [key]: defaults[key] || "" }));
  };

  const resetAll = async () => {
    if (!window.confirm("Visszaállítja az ÖSSZES szöveget az alapértelmezett értékekre? Ez nem vonható vissza.")) return;
    try {
      const { data } = await api.post("/admin/site-content/reset");
      setValues(data.values || {});
      toast.success("Alapértelmezett szövegek visszaállítva");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return <div className="card-elev p-8 text-center text-sm" style={{ color: "var(--text-mute)" }}>Betöltés...</div>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-wide">Tartalomkezelő</h1>
          <p className="text-sm" style={{ color: "var(--text-mute)" }}>
            Szerkesztheti az oldal összes statikus szövegét. A változtatások mentés után azonnal érvényesülnek.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetAll} className="btn-ghost" data-testid="reset-all-content-btn">
            <RotateCcw size={12} /> Összes alapértelmezett
          </button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-content-btn">
            <Save size={14} /> {saving ? "Mentés..." : "Mentés"}
          </button>
        </div>
      </div>
      <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

      <EmailDiagnostics />

      <div className="space-y-5">
        {GROUPS.map((g) => (
          <section key={g.label} className="card-elev overflow-hidden" data-testid={`content-section-${g.label}`}>
            <div className="section-title">{g.label}</div>
            <div className="p-5 space-y-4">
              {g.fields.map((f) => {
                const val = values[f.key] ?? "";
                const def = defaults[f.key] ?? "";
                const isDirty = val !== def;
                return (
                  <div key={f.key} className="grid sm:grid-cols-[220px_1fr] gap-3 sm:items-start">
                    <div>
                      <label className="text-sm font-bold block">{f.label}</label>
                      <div className="text-[10px] font-mono" style={{ color: "var(--text-disabled)" }}>{f.key}</div>
                      {f.hint && <div className="text-[10px]" style={{ color: "var(--text-mute)" }}>{f.hint}</div>}
                    </div>
                    <div>
                      {f.textarea ? (
                        <textarea
                          className="input"
                          rows={3}
                          value={val}
                          onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                          placeholder={f.placeholder || def}
                          data-testid={`content-field-${f.key}`}
                        />
                      ) : (
                        <input
                          className="input"
                          value={val}
                          onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                          placeholder={f.placeholder || def}
                          data-testid={`content-field-${f.key}`}
                        />
                      )}
                      {isDirty && (
                        <button
                          type="button"
                          onClick={() => resetField(f.key)}
                          className="text-[11px] mt-1 hover:underline"
                          style={{ color: "var(--brand)" }}
                          data-testid={`reset-field-${f.key}`}
                        >
                          ↺ Alapértelmezett: "{def}"
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-content-btn-bottom">
          <Save size={14} /> {saving ? "Mentés..." : "Mentés"}
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// E-mail diagnostics panel — shown at the top of the content admin page.
// Hits POST /admin/email/test and renders the live SMTP config + the
// result of a real test send. Lets the admin figure out why email isn't
// going out in production without needing shell access.
// --------------------------------------------------------------------------
function EmailDiagnostics() {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!to || !/^.+@.+\..+$/.test(to)) {
      toast.error("Adj meg egy érvényes e-mail címet.");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/email/test", { to });
      setResult(data);
      if (data.success) {
        toast.success(`Teszt e-mail elküldve: ${data.sent_to}`);
      } else {
        toast.warning(`E-mail hiba: ${data.error || "ismeretlen"}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="card-elev overflow-hidden mb-5" data-testid="email-diagnostics">
      <div
        className="section-title cursor-pointer flex items-center justify-between"
        onClick={() => setOpen((s) => !s)}
        style={{ background: "linear-gradient(180deg, #0f766e 0%, #134e4a 100%)" }}
      >
        <span className="flex items-center gap-2"><Mail size={14} /> E-mail küldés diagnosztika</span>
        <span className="text-[10px] opacity-80">{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div className="p-4 bg-white text-sm space-y-3">
          <div style={{ color: "var(--text-mute)" }} className="text-xs">
            Küldj egy teszt üzenetet. A rendszer megmutatja a jelenlegi SMTP konfigurációt és a részletes hibát,
            ha nem sikerül kézbesíteni. Használd production-ön is, ha az e-mailek nem érkeznek meg.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="email"
              className="input !py-1.5 flex-1 min-w-[220px] text-sm"
              placeholder="pl. te@gmail.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={running}
              data-testid="email-test-to"
            />
            <button
              onClick={run}
              disabled={running}
              className="btn-primary"
              data-testid="email-test-run"
            >
              {running ? (<><Loader2 size={12} className="animate-spin" /> Küldés…</>) : (<><Mail size={12} /> Teszt küldése</>)}
            </button>
          </div>
          {result && (
            <div
              className="p-3 rounded text-xs"
              style={{
                background: result.success ? "#E6F4EA" : "#FDECEC",
                border: `1px solid ${result.success ? "#0A8043" : "#c8102e"}`,
                color: result.success ? "#0A8043" : "#c8102e",
              }}
              data-testid="email-test-result"
            >
              <div className="flex items-center gap-2 font-bold mb-2">
                {result.success ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                {result.success
                  ? `Sikeres kézbesítés: ${result.sent_to}`
                  : `Hiba (${result.stage}): ${result.error}${result.error_detail ? ` — ${result.error_detail}` : ""}`}
              </div>
              {result.missing && result.missing.length > 0 && (
                <div className="mb-2">
                  <strong>Hiányzó env változók:</strong> {result.missing.join(", ")}
                </div>
              )}
              {result.hint && (
                <div className="mb-2" style={{ color: "var(--text)" }}><strong>Tipp:</strong> {result.hint}</div>
              )}
              {result.config && (
                <div className="font-mono text-[10px]" style={{ color: "var(--text)" }}>
                  <div className="font-bold uppercase mb-1">Jelenlegi SMTP konfiguráció:</div>
                  <ul className="space-y-0.5">
                    <li>GMAIL_USER: {result.config.GMAIL_USER_value || <em style={{ color: "#c8102e" }}>(üres!)</em>}</li>
                    <li>GMAIL_APP_PASSWORD: {result.config.GMAIL_APP_PASSWORD_set
                      ? `✓ beállítva (${result.config.GMAIL_APP_PASSWORD_length} karakter)`
                      : <em style={{ color: "#c8102e" }}>✗ nincs beállítva!</em>}</li>
                    <li>SMTP_SERVER: {result.config.SMTP_SERVER}</li>
                    <li>SMTP_PORT: {result.config.SMTP_PORT} ({result.config.SMTP_PORT === 465 ? "SSL" : "STARTTLS"})</li>
                    <li>EMAIL_SENDER_NAME: {result.config.EMAIL_SENDER_NAME}</li>
                    <li>ORDER_NOTIFY_EMAIL: {result.config.ORDER_NOTIFY_EMAIL || "—"}</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

